import apiClient from "./axiosConfig";

/** Sent on `window` when a delete the editor chose to keep as a draft has been switched off, so a view that
 * took the item out of its lists can load it again. `detail.url` is the item's URL. */
export const CONTENT_DRAFTED_EVENT = "arrows:content-drafted";

/**
 * Determine the parent resource URL for ordering endpoints.
 * Ordering endpoints are PATCH-only and require the parent resource's ETag.
 *
 * @param {string} url - The endpoint URL
 * @returns {string} The parent resource URL to fetch ETag from
 */
function getEtagSourceUrl(url) {
// Ordering endpoints require parent's ETag (see docs/api/backoffice.md)
  if (url.endsWith('/steps-order/')) {
    // /lesson-versions/{id}/steps-order/ -> /lesson-versions/{id}/
    return url.replace(/\/steps-order\/$/, '/');
  }
  if (url.endsWith('/state-conditions-order/')) {
    // /scenario-states/{id}/state-conditions-order/ -> /scenario-states/{id}/
    return url.replace(/\/state-conditions-order\/$/, '/');
  }
  if (url.endsWith('/content-order/')) {
    // /units/{id}/content-order/ -> /units/{id}/
    return url.replace(/\/content-order\/$/, '/');
  }
  if (url.endsWith('/units-order/')) {
    // /courses/{id}/units-order/ -> /courses/{id}/
    return url.replace(/\/units-order\/$/, '/');
  }
  // For regular resource endpoints, fetch from the same URL
  return url;
}

/**
 * Extract ETag from response headers (handles case variations).
 *
 * @param {object} headers - Axios response headers
 * @returns {string|null} The ETag value or null if not found
 */
export function extractEtag(headers) {
  return headers?.etag || headers?.ETag || headers?.Etag || null;
}

/**
 * Fetch current ETag for a resource and perform a conditional PATCH.
 * This prevents the "fetch ETag, then patch" boilerplate throughout the codebase.
 *
 * For ordering endpoints (e.g., /units/{id}/content-order/), this automatically
 * fetches the ETag from the parent resource (e.g., /units/{id}/) as required by
 * the API design.
 *
 * @param {string} url - The resource URL (or ordering endpoint URL)
 * @param {object} payload - The data to send in the PATCH request
 * @param {{ etag?: string | null }} [options] - If `etag` is set, use it for If-Match (optimistic
 *   concurrency from editor state). If omitted or null/empty, GET first (last-writer behavior).
 * @returns {Promise<object>} The axios response
 * @throws {Error} If the GET or PATCH fails
 */
export async function patchWithEtag(url, payload, options = {}) {
  try {
    let etag = options.etag;
    if (etag == null || etag === "") {
      const etagUrl = getEtagSourceUrl(url);
      const getResp = options.timeout != null
        ? await apiClient.get(etagUrl, { timeout: options.timeout })
        : await apiClient.get(etagUrl);
      etag = extractEtag(getResp.headers);
    }

    const config = etag
      ? {
          headers: {
            "If-Match": etag,
          },
        }
      : {};

    if (options.timeout != null) config.timeout = options.timeout;
    return await apiClient.patch(url, payload, config);
  } catch (error) {
    console.error(`patchWithEtag failed for ${url}:`, error);
    throw error;
  }
}

/**
 * Fetch current ETag for a resource and build If-Match headers.
 * Useful when you need the ETag but want to control the request yourself.
 *
 * @param {string} url - The resource URL
 * @returns {Promise<{etag: string|null, headers: object}>}
 */
export async function getEtagHeaders(url) {
  try {
    const getResp = await apiClient.get(url);
    const etag = extractEtag(getResp.headers);

    return {
      etag,
      headers: etag ? { 'If-Match': etag } : {}
    };
  } catch (error) {
    console.error(`getEtagHeaders failed for ${url}:`, error);
    throw error;
  }
}
